// common/hooks/useSwipeNavigation.ts (веб)
import { useMemo, useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import {
  selectAllSaleItems,
  upsertItems,
} from "../../store/slices/saleItemListSlice";
import { getApi } from "../../api/api";

interface SwipeableItem {
  id: number;
  categoryId?: number | string;
}

export const useSwipeNavigation = (
  currentItemId: number,
  categoryId?: number,
) => {
  const dispatch = useDispatch();
  const allItems = useSelector(selectAllSaleItems);
  const totalItems = useSelector(
    (state: RootState) => state.saleItemList.total,
  );
  const [loading, setLoading] = useState(false);

  const items = useMemo(() => {
    if (categoryId) {
      return allItems.filter(
        (item) => Number(item.categoryId) === Number(categoryId),
      ) as SwipeableItem[];
    }
    return allItems as SwipeableItem[];
  }, [allItems, categoryId]);

  const currentIndex = items.findIndex((item) => item.id === currentItemId);
  const isLastLoaded = currentIndex === items.length - 1;
  const hasMoreOnServer = totalItems ? items.length < totalItems : false;
  const needsLoadMore =
    isLastLoaded && hasMoreOnServer && currentIndex >= 0 && !loading;

  const loadMore = useCallback(async () => {
    if (loading || !needsLoadMore) return;
    setLoading(true);
    try {
      const api = getApi();
      const offset = allItems.length;
      const response = await api.getSaleItemList({
        limit: 50,
        offset,
        ...(categoryId && { categoryId }),
      });
      if (response?.data) {
        dispatch(upsertItems(response.data));
      }
    } catch (error) {
      console.error("SwipeNavigation load more error:", error);
    } finally {
      setLoading(false);
    }
  }, [allItems.length, categoryId, dispatch, loading, needsLoadMore]);

  useEffect(() => {
    if (needsLoadMore) {
      loadMore();
    }
  }, [needsLoadMore, loadMore]);

  return {
    items,
    currentIndex,
    hasPrev: currentIndex > 0,
    hasNext: currentIndex < items.length - 1,
    prevItem: currentIndex > 0 ? items[currentIndex - 1] : null,
    nextItem: currentIndex < items.length - 1 ? items[currentIndex + 1] : null,
  };
};
